import { useState } from "react";
import axios from "axios";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Reveal, ChapterHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    "New custom website",
    "Website redesign",
    "Landing page",
    "E-commerce store",
    "Something else",
];

const BUDGET_OPTIONS = [
    "$150 – $300",
    "$300 – $500",
    "$500 – $750",
    "$750 – $2,500",
    "Not sure yet",
];

const INITIAL_FORM = {
    name: "",
    email: "",
    business_name: "",
    service: "",
    budget: "",
    message: "",
};

const inputClass =
    "w-full rounded-xl border border-slate-200 bg-mist px-4 py-3 text-sm text-ink placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/15";

const Contact = () => {
    const [form, setForm] = useState(INITIAL_FORM);
    const [sending, setSending] = useState(false);

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message sent. I'll get back to you within 24 hours.");
            setForm(INITIAL_FORM);
        } catch (err) {
            toast.error(
                "Something went wrong sending your message. Please try again or email me directly."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="border-t border-slate-200/80 bg-mist py-20 sm:py-28 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
                    <div className="lg:col-span-5">
                        <ChapterHeading
                            testId="contact-heading"
                            chapter="05"
                            eyebrow="Start a Project"
                            title="Tell me about your project."
                            description="Share a few details below and I'll reply with honest thoughts, a rough timeline, and a clear quote, usually within one working day."
                        />
                        <Reveal delay={0.22}>
                            <div className="mt-10 space-y-4">
                                <a
                                    href="mailto:csstudiowebdesign21@gmail.com"
                                    data-testid="contact-email-link"
                                    className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-colors duration-300 hover:border-brand/30 hover:bg-ice"
                                >
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ice text-brand-dark transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                                        <Mail className="h-5 w-5" />
                                    </span>
                                    <span>
                                        <span className="block text-xs font-medium text-slate-500">
                                            Prefer email?
                                        </span>
                                        <span className="block text-sm font-semibold text-ink">
                                            csstudiowebdesign21@gmail.com
                                        </span>
                                    </span>
                                </a>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.15} className="lg:col-span-7">
                        <form
                            data-testid="contact-form"
                            onSubmit={handleSubmit}
                            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_48px_-32px_rgba(15,23,42,0.15)] sm:p-10"
                        >
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor="contact-name"
                                        className="mb-2 block text-sm font-semibold text-ink"
                                    >
                                        Name <span className="text-brand">*</span>
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-input-name"
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Your name"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="contact-email"
                                        className="mb-2 block text-sm font-semibold text-ink"
                                    >
                                        Email <span className="text-brand">*</span>
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-input-email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@company.com"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="contact-business"
                                        className="mb-2 block text-sm font-semibold text-ink"
                                    >
                                        Business name
                                    </label>
                                    <input
                                        id="contact-business"
                                        data-testid="contact-input-business"
                                        type="text"
                                        value={form.business_name}
                                        onChange={update("business_name")}
                                        placeholder="Your business or brand"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="contact-service"
                                        className="mb-2 block text-sm font-semibold text-ink"
                                    >
                                        What do you need?{" "}
                                        <span className="text-brand">*</span>
                                    </label>
                                    <select
                                        id="contact-service"
                                        data-testid="contact-select-service"
                                        required
                                        value={form.service}
                                        onChange={update("service")}
                                        className={`${inputClass} appearance-none ${form.service ? "" : "text-slate-400"}`}
                                    >
                                        <option value="" disabled>
                                            Select a service
                                        </option>
                                        {SERVICE_OPTIONS.map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label
                                        htmlFor="contact-budget"
                                        className="mb-2 block text-sm font-semibold text-ink"
                                    >
                                        Budget <span className="text-brand">*</span>
                                    </label>
                                    <div
                                        className="flex flex-wrap gap-2.5"
                                        role="radiogroup"
                                        aria-label="Budget"
                                    >
                                        {BUDGET_OPTIONS.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                data-testid={`contact-budget-${option
                                                    .toLowerCase()
                                                    .replace(/[^a-z0-9]+/g, "-")}`}
                                                onClick={() =>
                                                    setForm((f) => ({ ...f, budget: option }))
                                                }
                                                aria-pressed={form.budget === option}
                                                className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                                                    form.budget === option
                                                        ? "border-brand bg-brand text-white"
                                                        : "border-slate-200 bg-mist text-slate-600 hover:border-brand/40 hover:bg-ice"
                                                }`}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        id="contact-budget"
                                        data-testid="contact-select-budget"
                                        type="text"
                                        required
                                        value={form.budget}
                                        onChange={() => {}}
                                        className="sr-only"
                                        aria-hidden="true"
                                        tabIndex={-1}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label
                                        htmlFor="contact-message"
                                        className="mb-2 block text-sm font-semibold text-ink"
                                    >
                                        Message <span className="text-brand">*</span>
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        data-testid="contact-input-message"
                                        required
                                        rows={5}
                                        value={form.message}
                                        onChange={update("message")}
                                        placeholder="Tell me about your business, what your current site is missing, and what you'd like the new one to achieve."
                                        className={`${inputClass} resize-none`}
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-form-submit-button"
                                disabled={sending}
                                className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {sending ? (
                                    <>
                                        Sending
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        Send Message
                                        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </>
                                )}
                            </button>
                            <p className="mt-4 text-xs text-slate-400">
                                Your details are only used to reply to your enquiry. Never shared, never spammed.
                            </p>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
